import axios from "axios";
import { Message, MessageBox } from "element-ui";
import { refreshToken, logout } from "@/api/login";

// 设置默认请求头
axios.defaults.headers["Content-Type"] = "application/json;charset=utf-8";

// 请求队列：保存待重试的请求 { config, resolve, reject }
let requestQueue = [];
// 是否正在刷新 token
let isRefreshing = false;

// 创建 axios 实例
const service = axios.create({
	baseURL: process.env.VUE_APP_BASE_API + "/admin-api",
	timeout: 30000
});

// 请求拦截器
service.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem("ACCESS_TOKEN");
		if (token) {
			config.headers["Authorization"] = "Bearer " + token;
		}
		config.headers["tenant-id"] = 1;

		// 处理 GET 请求的嵌套参数（兼容对象）
		if (config.method === "get" && config.params) {
			let url = config.url + "?";
			for (const key of Object.keys(config.params)) {
				const value = config.params[key];
				if (value == null) continue;

				const encode = (k, v) =>
					encodeURIComponent(k) + "=" + encodeURIComponent(v);

				if (typeof value === "object") {
					for (const subKey of Object.keys(value)) {
						url += encode(`${key}[${subKey}]`, value[subKey]) + "&";
					}
				} else {
					url += encode(key, value) + "&";
				}
			}
			config.url = url.slice(0, -1);
			config.params = {};
		}

		return config;
	},
	(error) => {
		console.error("请求拦截错误:", error);
		return Promise.reject(error);
	}
);

// 显示重新登录弹窗
async function showReLogin() {
	try {
		await MessageBox.confirm("登录状态已过期，请重新登录", "系统提示", {
			confirmButtonText: "重新登录",
			showCancelButton: false,
			showClose: false,
			type: "warning"
		});
		await logout();
		localStorage.clear();
		location.href = "/";
	} catch (e) {
		// 用户关闭弹窗等操作，静默处理
	}
}

// 响应拦截器
service.interceptors.response.use(
	async (response) => {
		const { code, msg } = response.data || {};

		if (code === 200) {
			return response.data;
		}

		// Token 过期或未认证
		if (code === 401) {
			const originalRequest = response.config;

			// 如果没有 refresh token，直接登出
			const refreshTokenValue = localStorage.getItem("REFRESH_TOKEN");
			if (!refreshTokenValue) {
				await showReLogin();
				return Promise.reject(new Error("No refresh token"));
			}

			if (!isRefreshing) {
				isRefreshing = true;

				try {
					// 尝试刷新 token
					const res = await refreshToken();
					const { accessToken, refreshToken: newRefreshToken } = res.data;

					// 更新本地 token
					localStorage.setItem("ACCESS_TOKEN", accessToken);
					localStorage.setItem("REFRESH_TOKEN", newRefreshToken);

					// 重放队列中的所有请求
					requestQueue.forEach(({ config, resolve }) => {
						config.headers["Authorization"] = "Bearer " + accessToken;
						resolve(service(config));
					});

					// 重放当前请求
					originalRequest.headers["Authorization"] = "Bearer " + accessToken;
					return service(originalRequest);
				} catch (error) {
					// 刷新失败，清空并跳转登录
					await showReLogin();
					return Promise.reject(new Error("刷新token失败")); // 补充return，避免Promise状态异常
				} finally {
					requestQueue = [];
					isRefreshing = false;
				}
			}

			// 正在刷新中，将当前请求加入队列
			return new Promise((resolve, reject) => {
				requestQueue.push({ config: originalRequest, resolve, reject });
			});
		}

		// 其他特定业务错误（如 406）
		if (code === 406) {
			await showReLogin();
			return Promise.reject(new Error("Session expired (406)"));
		}

		// 通用业务错误提示
		Message.error(msg || "操作失败");
		return Promise.reject(response.data);
	},
	(error) => {
		// 优化：精准区分错误类型，针对500给出专属提示
		console.error("响应拦截错误:", error);
		let errorMsg = "网络请求失败，请稍后重试";

		// 有HTTP响应的错误（如500、404、403等）
		if (error.response) {
			const { status } = error.response;
			switch (status) {
				case 500:
					errorMsg = "服务器内部错误，请稍后重试";
					break;
				case 404:
					errorMsg = "请求的接口不存在";
					break;
				case 403:
					errorMsg = "暂无权限访问该接口";
					break;
				case 408:
					errorMsg = "请求超时，请检查网络";
					break;
				default:
					errorMsg = `请求失败（${status}）：${
						error.response.data?.msg || "未知错误"
					}`;
			}
		}
		// 无响应（网络断开、超时）
		else if (error.request) {
			errorMsg = "请求超时，请检查网络连接";
		}
		// 请求配置错误
		else {
			errorMsg = error.message || "请求参数错误";
		}

		// 仅显示Element轻提示，无全屏overlay
		Message.error(errorMsg);
		return Promise.reject(error);
	}
);

export default service;
